/**
 * The audio engine: two soft quasi-organ voices and a metronome, synthesised on
 * the fly. No samples, so the app stays a single static bundle and works
 * offline the first time it loads.
 *
 * The engine owns no musical state — it is told what pitch to sound, when, and
 * for how long. Playback scheduling lives in `usePlayback`, which schedules one
 * measure at a time so a tempo change takes effect on the very next measure.
 */

/** How the metronome click is voiced. Exposed in settings. */
export type ClickVoice = 'wood' | 'beep' | 'rim';

export interface ClickVoiceSpec {
  readonly id: ClickVoice;
  readonly label: string;
  readonly frequency: number;
  readonly decay: number;
  readonly type: OscillatorType;
  /** Amount of filtered noise mixed in, 0–1. */
  readonly noise: number;
}

/** The metronome voices, in menu order. */
export const CLICK_VOICES: readonly ClickVoiceSpec[] = [
  { id: 'wood', label: 'Wood block', frequency: 1400, decay: 0.055, type: 'triangle', noise: 0.5 },
  { id: 'beep', label: 'Beep', frequency: 1900, decay: 0.045, type: 'sine', noise: 0 },
  { id: 'rim', label: 'Rim', frequency: 720, decay: 0.09, type: 'square', noise: 0.75 },
];

/** Pan positions, so the two voices are separable by ear. */
const PAN_CF = -0.28;
const PAN_CP = 0.28;

type Chain = { oscillators: OscillatorNode[]; gain: GainNode };

/**
 * A lazily-created Web Audio graph.
 *
 * Browsers refuse to start an AudioContext before a user gesture, so the
 * context is built on the first sound and resumed on every use — which is
 * exactly what iOS Safari and Android Chrome want.
 */
class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private live: Chain[] = [];
  private noiseBuffer: AudioBuffer | null = null;

  /** Master volume, 0–1. Persisted in settings. */
  volume = 0.9;

  /** Metronome volume relative to the voices, 0–1. */
  clickVolume = 0.6;

  /** Which click voice the metronome uses. */
  clickVoice: ClickVoice = 'wood';

  /** The audio context, created on demand. Returns null if audio is unavailable. */
  private context(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;
      if (!Ctor) return null;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    if (this.master) this.master.gain.value = this.volume;
    return this.ctx;
  }

  /** Current audio clock, or 0 before the context exists. */
  now(): number {
    return this.context()?.currentTime ?? 0;
  }

  /** Wake the context from a user gesture, before anything needs to sound. */
  unlock(): void {
    this.context();
  }

  /**
   * Sound one note.
   *
   * @param midi     MIDI note number
   * @param at       audio-clock time to start at
   * @param duration seconds, envelope included
   * @param voice    which side of the stereo field to place it on
   */
  note(midi: number, at: number, duration: number, voice: 'cf' | 'cp'): void {
    const ctx = this.context();
    if (!ctx || !this.master) return;
    const freq = 440 * Math.pow(2, (midi - 69) / 12);

    const gain = ctx.createGain();
    const lowpass = ctx.createBiquadFilter();
    const panner = ctx.createStereoPanner();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 1900;
    panner.pan.value = voice === 'cf' ? PAN_CF : PAN_CP;

    // A gentle organ envelope: quick swell, flat body, short release.
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(0.16, at + 0.03);
    gain.gain.setValueAtTime(0.16, at + Math.max(0.06, duration - 0.12));
    gain.gain.linearRampToValueAtTime(0.0001, at + duration);

    const chain: Chain = { oscillators: [], gain };
    for (const [type, detune, amount] of [
      ['sine', 0, 1],
      ['triangle', 4, 0.5],
    ] as const) {
      const osc = ctx.createOscillator();
      const oscGain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      osc.detune.value = detune;
      oscGain.gain.value = amount;
      osc.connect(oscGain);
      oscGain.connect(gain);
      osc.start(at);
      osc.stop(at + duration + 0.05);
      chain.oscillators.push(osc);
    }

    gain.connect(lowpass);
    lowpass.connect(panner);
    panner.connect(this.master);
    this.live.push(chain);
    chain.oscillators[0].onended = () => {
      const i = this.live.indexOf(chain);
      if (i >= 0) this.live.splice(i, 1);
    };
  }

  /** One metronome click. Used by tap-tempo and, if enabled, during playback. */
  click(at: number = this.now() + 0.01, accent = false): void {
    const ctx = this.context();
    if (!ctx || !this.master) return;
    const spec = CLICK_VOICES.find((v) => v.id === this.clickVoice) ?? CLICK_VOICES[0];
    const level = this.clickVolume * (accent ? 0.36 : 0.26);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(level, at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + spec.decay);
    gain.connect(this.master);

    const osc = ctx.createOscillator();
    osc.type = spec.type;
    osc.frequency.setValueAtTime(spec.frequency * (accent ? 1.25 : 1), at);
    osc.frequency.exponentialRampToValueAtTime(spec.frequency * 0.6, at + spec.decay);
    osc.connect(gain);
    osc.start(at);
    osc.stop(at + spec.decay + 0.02);

    if (spec.noise > 0) {
      const src = ctx.createBufferSource();
      src.buffer = this.noise(ctx);
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = spec.frequency;
      bp.Q.value = 1.4;
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(level * spec.noise, at);
      ng.gain.exponentialRampToValueAtTime(0.0001, at + spec.decay);
      src.connect(bp);
      bp.connect(ng);
      ng.connect(this.master);
      src.start(at);
      src.stop(at + spec.decay + 0.02);
    }
  }

  /** A short white-noise buffer, reused for every click. */
  private noise(ctx: AudioContext): AudioBuffer {
    if (!this.noiseBuffer) {
      const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.2), ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buffer;
    }
    return this.noiseBuffer;
  }

  /**
   * Cut everything that is sounding without a click: duck the master, stop the
   * oscillators, then reopen the master once the tails are gone.
   */
  silence(): void {
    if (!this.ctx || !this.master || !this.live.length) return;
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setTargetAtTime(0, t, 0.02);
    const chains = this.live.splice(0);
    for (const chain of chains) {
      for (const osc of chain.oscillators) {
        try {
          osc.stop(t + 0.08);
        } catch {
          /* already stopped */
        }
      }
    }
    window.setTimeout(() => {
      if (!this.ctx || !this.master) return;
      for (const chain of chains) {
        try {
          chain.gain.disconnect();
        } catch {
          /* already disconnected */
        }
      }
      this.master.gain.cancelScheduledValues(this.ctx.currentTime);
      this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.02);
    }, 120);
  }
}

/** The one engine instance. Audio hardware is not worth having two of. */
export const audio = new AudioEngine();

/* ---- tap tempo ---------------------------------------------------------- */

/** Tempo bounds, in semibreves (whole notes) per minute. */
export const TEMPO_MIN = 20;
export const TEMPO_MAX = 100;

/**
 * Turn a series of tap timestamps into a tempo, in semibreves per minute.
 *
 * Only the last few gaps count, so the reading follows a tempo that is still
 * settling. A whole note is slow — 40 a minute is a comfortable exercise pace —
 * so most people tap something faster and mean it as a subdivision. Rather than
 * rejecting that, the rate is folded by octaves into the usable range: tapping
 * 120 sets 60, tapping 15 sets 30. Returns null until two taps have landed, or
 * when even folding cannot bring the rate into range.
 */
export function tapTempo(taps: readonly number[]): number | null {
  if (taps.length < 2) return null;
  const recent = taps.slice(-5);
  const gaps: number[] = [];
  for (let i = 1; i < recent.length; i++) gaps.push(recent[i] - recent[i - 1]);
  const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  if (mean <= 0) return null;

  let bpm = 60000 / mean;
  for (let i = 0; i < 4 && bpm > TEMPO_MAX; i++) bpm /= 2;
  for (let i = 0; i < 4 && bpm < TEMPO_MIN; i++) bpm *= 2;
  if (bpm < TEMPO_MIN || bpm > TEMPO_MAX) return null;
  return Math.round(bpm);
}

/** A tap this long after the previous one starts a new series. */
export const TAP_TIMEOUT_MS = 2500;
